import React from 'react';
import {
  Box, Button, Card, CardActionArea, CardContent, Chip, Paper, Stack, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

export const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

export const rentalStatusColor = (status) => {
  const map = {
    Active: 'success',
    Overdue: 'error',
    Completed: 'info',
    Cancelled: 'default',
    pending: 'warning',
    partial: 'info',
    paid: 'success',
    overdue: 'error',
    cancelled: 'default',
    rented: 'success',
    returned: 'info',
    damaged: 'error',
    good: 'success'
  };
  return map[status] || 'default';
};

export const headerSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: 2,
  mb: 2.5,
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  background: (t) => (t.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(66,165,245,0.14) 0%, rgba(27,36,52,0.95) 70%)'
    : 'linear-gradient(135deg, #e3f2fd 0%, #f7fbff 48%, #ffffff 100%)'),
  border: '1px solid',
  borderColor: 'divider'
};

export const sectionSx = {
  p: { xs: 1.5, md: 2.25 },
  mb: 2,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none'
};

export const tableSx = {
  '& thead th': {
    bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f4f7fb'),
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 0.2,
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid',
    borderColor: 'divider'
  },
  '& tbody td': { py: 1.15, fontSize: 13 },
  '& tbody tr:last-of-type td': { borderBottom: 0 }
};

export const RentalPageHeader = ({ title, subtitle, actions, backTo, backLabel = 'Back' }) => {
  const navigate = useNavigate();
  return (
    <Box sx={headerSx}>
      <Box sx={{ minWidth: 0 }}>
        {backTo ? (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backTo)}
            sx={{ mb: 0.75, ml: -0.5, color: 'text.secondary' }}
          >
            {backLabel}
          </Button>
        ) : null}
        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.3 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Box sx={{ mt: 0.5, maxWidth: 720, color: 'text.secondary', typography: 'body2' }}>
            {subtitle}
          </Box>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
};

export const RentalStatusChip = ({ status, size = 'small' }) => (
  <Chip
    size={size}
    label={status || '—'}
    color={rentalStatusColor(status)}
    sx={{ fontWeight: 600, textTransform: 'capitalize' }}
  />
);

export const RentalSourceChip = ({ source, label }) => {
  const outside = source === 'outside' || /outside/i.test(label || '');
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label || (outside ? 'Rent from Outside' : 'Sun Systems')}
      sx={{
        fontWeight: 600,
        borderColor: outside ? 'warning.main' : 'primary.main',
        color: outside ? 'warning.dark' : 'primary.main',
        bgcolor: (t) => (t.palette.mode === 'dark'
          ? 'transparent'
          : (outside ? 'rgba(237,108,2,0.08)' : 'rgba(25,118,210,0.08)'))
      }}
    />
  );
};

export const RentalStatCard = ({
  label, value, extra, accent = 'primary', onClick
}) => {
  const color = `${accent}.main`;
  const inner = (
    <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ mt: 0.35, lineHeight: 1.25, wordBreak: 'break-word' }}>
        {value}
      </Typography>
      {extra ? (
        <Typography variant="caption" color="text.secondary">{extra}</Typography>
      ) : null}
    </CardContent>
  );
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: color,
        boxShadow: 'none'
      }}
    >
      {onClick ? <CardActionArea sx={{ height: '100%' }} onClick={onClick}>{inner}</CardActionArea> : inner}
    </Card>
  );
};

export const RentalSection = ({ title, action, children, sx = {} }) => (
  <Paper sx={{ ...sectionSx, ...sx }}>
    {(title || action) ? (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        {title ? (
          <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
        ) : <span />}
        {action || null}
      </Box>
    ) : null}
    {children}
  </Paper>
);
